'use client'

import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useRouter } from 'next/router'

import { OUTLAY_REPORTING_START_FY } from '@/constants/usaspending'

import type {
  DetailFilterActions,
  DetailFilterState,
  RecipientAnalyticsFilters,
} from '@/types/usaspending'

/**
 * DetailFilterContext
 *
 * Shared filter state for recipient and agency detail pages.
 * Syncs filter state with URL params for shareability.
 *
 * Following Vercel composition patterns:
 * - State lifted into provider
 * - Generic interface for dependency injection
 * - Decoupled from implementation details
 */

export interface DetailFilterContextValue {
  state: DetailFilterState
  actions: DetailFilterActions
}

export const DetailFilterContext =
  createContext<DetailFilterContextValue | null>(null)

export function useDetailFilter(): DetailFilterContextValue {
  const context = useContext(DetailFilterContext)
  if (!context) {
    throw new Error(
      'useDetailFilter must be used within a DetailFilterProvider',
    )
  }
  return context
}

export interface DetailFilterProviderProps {
  children: ReactNode
  /** Default fiscal year if not in URL */
  defaultFiscalYear?: number | 'all'
}

function getSearchParamsFromAsPath(asPath: string): URLSearchParams {
  const cleanAsPath = asPath.split('#')[0]
  const queryStart = cleanAsPath.indexOf('?')
  return new URLSearchParams(
    queryStart >= 0 ? cleanAsPath.slice(queryStart + 1) : '',
  )
}

function parseDetailFilterState(
  searchParams: URLSearchParams,
  defaults: {
    fiscalYear: number | 'all'
  },
): DetailFilterState {
  const fyParam = searchParams.get('fy')
  const parsedFiscalYear =
    fyParam && fyParam !== 'all' ? Number.parseInt(fyParam, 10) : null
  const fiscalYear: number | 'all' =
    fyParam === 'all'
      ? 'all'
      : parsedFiscalYear !== null && !Number.isNaN(parsedFiscalYear)
        ? parsedFiscalYear
        : defaults.fiscalYear

  return { fiscalYear }
}

function writeDetailFilterState(
  params: URLSearchParams,
  state: DetailFilterState,
  defaults: {
    fiscalYear: number | 'all'
  },
) {
  if (state.fiscalYear === defaults.fiscalYear) {
    params.delete('fy')
  } else {
    params.set('fy', String(state.fiscalYear))
  }
}

export function toRecipientAnalyticsFilters(
  state: DetailFilterState,
  options?: { includeFiscalYear?: boolean },
): RecipientAnalyticsFilters {
  return {
    fiscalYear:
      options?.includeFiscalYear === false || state.fiscalYear === 'all'
        ? undefined
        : state.fiscalYear,
  }
}

export function DetailFilterProvider({
  children,
  defaultFiscalYear = 'all',
}: DetailFilterProviderProps) {
  const router = useRouter()
  const defaults = useMemo(
    () => ({
      fiscalYear: defaultFiscalYear,
    }),
    [defaultFiscalYear],
  )
  const [state, setState] = useState<DetailFilterState>(() =>
    parseDetailFilterState(getSearchParamsFromAsPath(router.asPath), defaults),
  )

  useEffect(() => {
    setState(
      parseDetailFilterState(
        getSearchParamsFromAsPath(router.asPath),
        defaults,
      ),
    )
  }, [defaults, router.asPath])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncFromUrl = () => {
      setState(
        parseDetailFilterState(
          new URLSearchParams(window.location.search),
          defaults,
        ),
      )
    }

    syncFromUrl()
    window.addEventListener('popstate', syncFromUrl)
    return () => {
      window.removeEventListener('popstate', syncFromUrl)
    }
  }, [defaults])

  const replaceUrl = useCallback(
    (nextState: DetailFilterState) => {
      if (typeof window === 'undefined') return

      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)
      writeDetailFilterState(params, nextState, defaults)
      const qs = params.toString()
      const nextUrl = `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`
      window.history.replaceState(window.history.state, '', nextUrl)
    },
    [defaults],
  )

  const updateState = useCallback(
    (updater: (previous: DetailFilterState) => DetailFilterState) => {
      setState((previous) => {
        const nextState = updater(previous)
        replaceUrl(nextState)
        return nextState
      })
    },
    [replaceUrl],
  )

  const actions: DetailFilterActions = useMemo(
    () => ({
      setFiscalYear: (fy: number | 'all') => {
        updateState((previous) => ({ ...previous, fiscalYear: fy }))
      },
      reset: () => {
        updateState(() => ({
          fiscalYear: defaults.fiscalYear,
        }))
      },
    }),
    [defaults.fiscalYear, updateState],
  )

  const value = useMemo(() => ({ state, actions }), [state, actions])

  return (
    <DetailFilterContext.Provider value={value}>
      {children}
    </DetailFilterContext.Provider>
  )
}

/**
 * Generate available fiscal years for filter dropdown.
 * Returns FY2022 onwards (DATA Act outlay reporting began Oct 2021 / FY2022).
 */
export function getAvailableFiscalYears(referenceMs = Date.now()): number[] {
  const now = new Date(referenceMs)
  const currentYear = now.getUTCFullYear()
  const currentMonth = now.getUTCMonth()
  // Fiscal year starts in October, so after Oct we're in the next FY
  const currentFY = currentMonth >= 9 ? currentYear + 1 : currentYear
  return Array.from(
    { length: currentFY - OUTLAY_REPORTING_START_FY + 1 },
    (_, i) => OUTLAY_REPORTING_START_FY + i,
  )
}
